import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { trackSignupCompletedOnce } from '@/lib/analytics';

import { Card, CardContent } from '@/components/ui/card';

import { Button } from '@/components/ui/button';
import { LegalAcceptanceCheckbox } from '@/components/legal/LegalAcceptanceCheckbox';
import { LegalDocDialog } from '@/components/legal/LegalDocDialog';
import { Heart, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';

// Key for storing pending invitation in sessionStorage
export const PENDING_INVITE_KEY = 'careos_pending_invite';

interface InvitationData {
  id: string;
  patient_id: string;
  role: string;
  expires_at: string;
  accepted_at: string | null;
  patient_nickname: string;
}

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [caregiverTermsAccepted, setCaregiverTermsAccepted] = useState(false);
  const [caregiverTermsOpen, setCaregiverTermsOpen] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInvitation();
    }
  }, [token]);

  const fetchInvitation = async () => {
    try {
      // Use secure function to get invitation by token
      const { data, error } = await supabase
        .rpc('get_invitation_by_token', { invitation_token: token });

      if (error) throw error;

      if (!data || data.length === 0) {
        setError(t('invite.notFound'));
        return;
      }

      setInvitation(data[0] as InvitationData);
    } catch (err: any) {
      console.error('Error fetching invitation:', err);
      setError(t('invite.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const isCaregiverInvite = invitation?.role === 'caregiver';

  const acceptInvitation = async () => {
    if (!user || !token) return;

    if (isCaregiverInvite && !caregiverTermsAccepted) {
      toast.error(t('invite.caregiverTerms.mustAccept') || 'É necessário aceitar o Termo de Uso do Cuidador');
      return;
    }

    setIsAccepting(true);

    try {
      // Use secure function to accept invitation
      const { data: success, error } = await supabase
        .rpc('accept_invitation', { invitation_token: token });

      if (error) {
        // Check for owner trying to accept invite for their own patient
        if (error.message?.includes('OWNER_CANNOT_ACCEPT_INVITE')) {
          setError(t('invite.ownerConflict'));
          return;
        }
        throw error;
      }

      if (!success) {
        toast.error(t('invite.invalid'));
        return;
      }

      // Add role to user_roles if not exists
      if (invitation) {
        const validRole = invitation.role as 'owner' | 'caregiver' | 'observer';

        const { data: existingRole } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', user.id)
          .eq('role', validRole)
          .maybeSingle();

        if (!existingRole) {
          await supabase
            .from('user_roles')
            .insert({
              user_id: user.id,
              role: validRole
            });
        }
      }

      // Clear the pending invite from sessionStorage
      sessionStorage.removeItem(PENDING_INVITE_KEY);

      // Register Caregiver Terms acceptance (LGPD / contractual)
      if (isCaregiverInvite) {
        try {
          await supabase.functions.invoke('record-acceptance', {
            body: { slugs: ['caregiver-terms'], method: 'web_click' },
          });
        } catch (err) {
          console.warn('[AcceptInvite] failed to record caregiver-terms acceptance', err);
        }
      }

      toast.success(t('invite.success'));

      // `invite_accepted` is emitted server-side (trigger on patient_user_links).
      // Covers caregivers/observers who just created their account via invite.
      trackSignupCompletedOnce(user, { audience: invitation?.role ?? 'caregiver' });


      navigate(`/app/patients/${invitation?.patient_id}`);

    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      toast.error(t('invite.acceptError'));
    } finally {
      setIsAccepting(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'caregiver': return t('invite.roles.caregiver');
      case 'observer': return t('invite.roles.observer');
      default: return role;
    }
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <CareOSLoader fullScreen label={t('common.loading')} />
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">{t('invite.ops')}</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.href = '/app'}>
              {t('invite.goToHome')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not logged in - redirect to auth with return URL
  if (!user) {
    const handleGoToAuth = () => {
      // Save the invite token in sessionStorage to persist across OAuth flow
      if (token) {
        sessionStorage.setItem(PENDING_INVITE_KEY, token);
      }
      // Also pass via URL for non-OAuth flow
      const returnUrl = `/invite/${token}`;
      navigate(`/auth?returnTo=${encodeURIComponent(returnUrl)}`);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-primary-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{t('invite.title')}</h2>
            <p className="text-muted-foreground mb-2">
              {t('invite.description', { role: getRoleLabel(invitation?.role || ''), patient: invitation?.patient_nickname })}
            </p>
            <Button className="w-full mt-4" onClick={handleGoToAuth}>
              {t('invite.loginToAccept')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in - show accept dialog
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('invite.acceptTitle')}</h2>
          <p className="text-muted-foreground mb-4">
            {t('invite.acceptDescription', { role: getRoleLabel(invitation?.role || ''), patient: invitation?.patient_nickname })}
          </p>

          {isCaregiverInvite && (
            <div className="mb-4 p-3 rounded-md bg-muted/40 text-left">
              <LegalAcceptanceCheckbox
                id="caregiver-terms-accept"
                checked={caregiverTermsAccepted}
                onCheckedChange={setCaregiverTermsAccepted}
              >
                {t('invite.caregiverTerms.prefix') || 'Li e concordo com o'}{' '}
                <button
                  type="button"
                  onClick={() => setCaregiverTermsOpen(true)}
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  {t('invite.caregiverTerms.docLabel') || 'Termo de Uso do Cuidador'}
                </button>
                {'. '}
                {t('invite.caregiverTerms.suffix') ||
                  'Compreendo que sou profissional autônomo e não mantenho vínculo empregatício com o CareOS.'}
              </LegalAcceptanceCheckbox>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => window.location.href = '/app'}
            >
              {t('invite.decline')}
            </Button>
            <Button
              className="flex-1"
              onClick={acceptInvitation}
              disabled={isAccepting || (isCaregiverInvite && !caregiverTermsAccepted)}
            >
              {isAccepting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('invite.accept')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <LegalDocDialog
        slug="caregiver-terms"
        fallbackTitle="Termo de Uso do Cuidador"
        open={caregiverTermsOpen}
        onOpenChange={setCaregiverTermsOpen}
        onAccept={() => setCaregiverTermsAccepted(true)}
      />
    </div>
  );
}
